/**
 * A vez de tocar — parte D1 (01/10/2026), spec §6.4.
 *
 * Com várias abas abertas, o mesmo aviso chega a todas; o som sai de UMA: a que detém
 * a trava `navigator.locks` "seweb:som-de-aviso". A aba só concorre depois do SEU 1º
 * gesto — uma aba recarregada e nunca clicada não tem o áudio liberado pelo navegador
 * e não pode "roubar" a vez de quem toca. Quem pega a trava fica com ela até fechar (a
 * promessa só resolve em `soltarTravaDoSom`); fechou, a próxima da fila assume.
 *
 * 🔴 "Sair" é navegação do SPA: o `NotificationProvider` desmonta, mas o DOCUMENTO fica. Sem
 *    soltar a trava no desmonte, a aba que saiu a segurava para sempre e, entrando de novo
 *    por outra aba, ninguém tocava (revisão final do D1, I1). `soltarTravaDoSom` é chamado
 *    na limpeza do efeito do 1º gesto; na volta do provider a aba concorre outra vez.
 *
 * ⚠️ `navigator.locks` só existe em contexto seguro (HTTPS ou localhost). Sem ele
 *    (http://IP, navegador antigo) toda aba que teve gesto toca e a janela de 5 s
 *    (`janelaDoSom`) segura o grosso — degradação aceita na spec.
 */

export const NOME_DA_TRAVA = "seweb:som-de-aviso";

export interface Travas {
  request: (nome: string, aoObter: () => Promise<void>) => Promise<unknown>;
}

let pediu = false;
let detem = false;
let semTrava = false;
/** Resolve a promessa que segura a trava (só existe enquanto esta aba a detém). */
let soltarSegurada: (() => void) | null = null;
/**
 * ⚠️ Cada pedido leva o número da sua geração. Soltar na FILA (sem ter a trava) não cancela o
 * `request`: quando a vez chegasse, o callback seguraria a trava para uma aba que já nem
 * concorre. A geração velha devolve a trava na hora.
 */
let geracao = 0;

function travasDoNavegador(): Travas | null {
  try {
    const travas = (navigator as unknown as { locks?: Travas | null }).locks;
    // happy-dom e navegador antigo: `locks` existe e vale null.
    return travas && typeof travas.request === "function" ? travas : null;
  } catch {
    return null;
  }
}

/** Chamado no 1º gesto da aba: daí em diante ela concorre à vez de tocar. */
export function concorrerAoSom(travas: Travas | null = travasDoNavegador()): void {
  if (pediu) return;
  pediu = true;
  geracao += 1;
  const minhaGeracao = geracao;
  if (!travas) {
    semTrava = true;
    return;
  }
  try {
    void travas
      .request(NOME_DA_TRAVA, () => {
        if (minhaGeracao !== geracao) return Promise.resolve();
        detem = true;
        return new Promise<void>((resolve) => {
          soltarSegurada = resolve;
        });
      })
      .catch(() => {
        if (minhaGeracao === geracao) semTrava = true;
      });
  } catch {
    semTrava = true;
  }
}

/**
 * Devolve a vez de tocar: o provider desmontou (sair da sessão). Resolve a promessa que
 * segura a trava, para a próxima aba da fila assumir, e esquece o estado: a volta do
 * provider registra o 1º gesto de novo e a aba concorre outra vez.
 */
export function soltarTravaDoSom(): void {
  geracao += 1;
  const soltar = soltarSegurada;
  soltarSegurada = null;
  pediu = false;
  detem = false;
  semTrava = false;
  soltar?.();
}

/** Esta aba é a que toca? */
export function estaAbaToca(): boolean {
  return pediu && (detem || semTrava);
}

/** Só para os testes. */
export function _reiniciarTravaParaTeste(): void {
  soltarTravaDoSom();
}
